// Context Provider Optimizer for the Behtari platform
// Resilient provider tree - each lazy provider fails independently

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';

// Core Providers (needed on every page)
import { UserProvider } from '@/contexts/UserContext';
import { PersonalizationProvider } from '@/contexts/PersonalizationContext';
import { LanguageProvider } from '@/contexts/LanguageContext';

// Platform Providers (lightweight, needed everywhere)
import { RTLProvider } from '@/components/Mobile/RTLLayoutManager';
import { ThemeProvider } from '@/components/ui/theme-provider';
import { ThemePreferenceSync } from '@/components/ui/ThemePreferenceSync';

// Lazy-loaded providers (only mount when needed)
const LazyErrorProvider = React.lazy(() => import('@/components/ErrorHandling/GlobalErrorHandler').then(m => ({ default: m.ErrorProvider })));
const LazyValidationProvider = React.lazy(() => import('@/components/Validation/FormValidation').then(m => ({ default: m.ValidationProvider })));

const LazyAnalyticsProvider = React.lazy(() => import('@/components/Analytics/AnalyticsProvider').then(m => ({ default: m.AnalyticsProvider })));
const LazyPrivacyManager = React.lazy(() => import('@/components/Privacy/PrivacyManager').then(m => ({ default: m.PrivacyManager })));
const LazySecurityHeaders = React.lazy(() => import('@/components/Security/SecurityHeaders').then(m => ({ default: m.SecurityHeaders })));
const LazyAccessibilityEnhancer = React.lazy(() => import('@/components/Accessibility/AccessibilityEnhancer').then(m => ({ default: m.AccessibilityEnhancer })));
const LazyServiceWorkerManager = React.lazy(() => import('@/components/PWA/ServiceWorkerManager').then(m => ({ default: m.ServiceWorkerManager })));
const LazyJobApplicationProvider = React.lazy(() => import('@/contexts/JobApplicationContext').then(m => ({ default: m.JobApplicationProvider })));
const LazyUniversalMessagingProvider = React.lazy(() => import('@/contexts/UniversalMessagingContext').then(m => ({ default: m.UniversalMessagingProvider })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Error boundary that falls back to unwrapped children when provider/chunk fails
class SafeProviderBoundary extends Component<
  { name: string; fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { name: string; fallback: ReactNode; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(`[SafeProvider] "${this.props.name}" failed to load, skipping provider:`, error.message, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Wraps a lazy provider so it can't crash the app
const SafeLazyProvider: React.FC<{
  name: string;
  Provider: React.LazyExoticComponent<React.FC<{ children: ReactNode }>>;
  children: ReactNode;
}> = ({ name, Provider, children }) => (
  <SafeProviderBoundary name={name} fallback={children}>
    <React.Suspense fallback={<>{children}</>}>
      <Provider>{children}</Provider>
    </React.Suspense>
  </SafeProviderBoundary>
);

// Core Platform Context - minimal providers for first paint
const CoreProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider defaultTheme="light" storageKey="behtari-theme">
    <LanguageProvider>
      <RTLProvider>
        <UserProvider>
          <PersonalizationProvider>
            <ThemePreferenceSync />
            {children}
          </PersonalizationProvider>
        </UserProvider>
      </RTLProvider>
    </LanguageProvider>
  </ThemeProvider>
);

// Deferred providers - each independently resilient
const DeferredProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SafeLazyProvider name="UniversalMessagingProvider" Provider={LazyUniversalMessagingProvider}>
  <SafeLazyProvider name="ErrorProvider" Provider={LazyErrorProvider}>
    <SafeLazyProvider name="SecurityHeaders" Provider={LazySecurityHeaders}>
      <SafeLazyProvider name="AccessibilityEnhancer" Provider={LazyAccessibilityEnhancer}>
        <SafeLazyProvider name="ServiceWorkerManager" Provider={LazyServiceWorkerManager}>
          <SafeLazyProvider name="PrivacyManager" Provider={LazyPrivacyManager}>
            <SafeLazyProvider name="JobApplicationProvider" Provider={LazyJobApplicationProvider}>
              <SafeLazyProvider name="ValidationProvider" Provider={LazyValidationProvider}>
                <SafeLazyProvider name="AnalyticsProvider" Provider={LazyAnalyticsProvider}>
                {children}
                </SafeLazyProvider>
              </SafeLazyProvider>
            </SafeLazyProvider>
          </SafeLazyProvider>
        </SafeLazyProvider>
      </SafeLazyProvider>
    </SafeLazyProvider>
  </SafeLazyProvider>
  </SafeLazyProvider>
);

// Optimized Context Composer - fast first paint with deferred heavy providers
export const OptimizedContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <CoreProviders>
        <DeferredProviders>
          {children}
        </DeferredProviders>
      </CoreProviders>
    </TooltipProvider>
  </QueryClientProvider>
);

export { CoreProviders };
